<template>
  <div class="rhe-wrap">

    <!-- Mode toggle -->
    <div class="rhe-mode-row">
      <button :class="['rhe-mode-btn', mode==='preset' ? 'rhe-mode-active' : '']" @click="mode='preset'">
        🎯 Choisir un preset
      </button>
      <button :class="['rhe-mode-btn', mode==='manual' ? 'rhe-mode-active' : '']" @click="mode='manual'">
        ✏️ Créer manuellement
      </button>
    </div>

    <!-- ── PRESET MODE ── -->
    <div v-if="mode==='preset'" class="rhe-preset-section">
      <div v-for="group in presetGroups" :key="group.level" class="rhe-preset-group">
        <div class="rhe-preset-group-label">{{ group.label }}</div>
        <div class="rhe-preset-cards">
          <div
            v-for="p in group.presets"
            :key="p.name"
            class="rhe-preset-card"
            :class="{ 'rhe-preset-selected': isSelectedPreset(p) }"
            @click="applyPreset(p)"
          >
            <!-- Mini board preview -->
            <div class="rhe-mini-board">
              <div
                v-for="v in p.vehicles"
                :key="v.id"
                class="rhe-mini-car"
                :style="miniCarStyle(v)"
              />
            </div>
            <div class="rhe-preset-name">{{ p.name }}</div>
            <div class="rhe-preset-moves">{{ p.moves }} coups</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── MANUAL MODE ── -->
    <template v-if="mode==='manual'">
      <!-- Toolbar -->
      <div class="rhe-toolbar">
        <div class="rhe-tool-row">
          <span class="rhe-label">Orientation</span>
          <button :class="['rhe-tb-btn', orientation==='H' ? 'rhe-active' : '']" @click="orientation='H'">↔ H</button>
          <button :class="['rhe-tb-btn', orientation==='V' ? 'rhe-active' : '']" @click="orientation='V'">↕ V</button>
        </div>
        <div class="rhe-tool-row">
          <span class="rhe-label">Taille</span>
          <button :class="['rhe-tb-btn', size===2 ? 'rhe-active' : '']" @click="size=2">🚗 2</button>
          <button :class="['rhe-tb-btn', size===3 ? 'rhe-active' : '']" @click="size=3">🚛 3</button>
        </div>
        <div class="rhe-tool-row">
          <span class="rhe-label">Cible 🎯</span>
          <button :class="['rhe-tb-btn', isTarget ? 'rhe-active' : '']" @click="toggleTarget">
            {{ isTarget ? '✅ Oui' : 'Non' }}
          </button>
        </div>
        <div v-if="!isTarget" class="rhe-tool-row">
          <span class="rhe-label">Couleur</span>
          <button
            v-for="c in PALETTE"
            :key="c"
            class="rhe-color-dot"
            :class="{ 'rhe-color-sel': color === c }"
            :style="{ background: c }"
            @click="color = c"
          />
        </div>
      </div>

      <!-- Board -->
      <div class="rhe-board-area">
        <div class="rhe-exit-arrow" :style="exitArrowStyle">▶</div>
        <div class="rhe-board">
          <div
            v-for="i in 36"
            :key="i"
            class="rhe-cell"
            @click="onCellClick(Math.floor((i-1)/6), (i-1)%6)"
          />
          <div
            v-for="v in localVehicles"
            :key="v.id"
            class="rhe-v-overlay"
            :style="vehicleOverlayStyle(v)"
            @click.stop="removeVehicle(v.id)"
            title="Cliquer pour supprimer"
          >
            <span class="rhe-v-x">✕</span>
            <span v-if="v.isTarget" class="rhe-v-flag">🎯</span>
          </div>
        </div>
      </div>

      <div class="rhe-footer">
        <button class="rhe-clear-btn" @click="clearAll">🗑 Tout effacer</button>
        <span class="rhe-hint">Cliquer sur une case vide pour placer · sur un véhicule pour supprimer</span>
      </div>
    </template>

  </div>
</template>

<script>
const PALETTE = ['#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#00acc1', '#6d4c41', '#fdd835', '#e91e63'];

const PRESETS = [
  // ── EASY ──────────────────────────────────────────────────────────────────
  {
    name: 'Facile 1', level: 'easy', moves: 2,
    vehicles: [
      { id:1, row:2, col:0, size:2, horizontal:true,  isTarget:true,  color:'#e53935' },
      { id:2, row:1, col:3, size:2, horizontal:false, isTarget:false, color:'#1e88e5' },
      { id:3, row:0, col:5, size:2, horizontal:false, isTarget:false, color:'#43a047' },
    ],
  },
  {
    name: 'Facile 2', level: 'easy', moves: 3,
    vehicles: [
      { id:1, row:2, col:2, size:2, horizontal:true,  isTarget:true,  color:'#e53935' },
      { id:2, row:1, col:4, size:2, horizontal:false, isTarget:false, color:'#fb8c00' },
      { id:3, row:1, col:5, size:2, horizontal:false, isTarget:false, color:'#8e24aa' },
      { id:4, row:0, col:0, size:2, horizontal:false, isTarget:false, color:'#00acc1' },
    ],
  },
  // ── MEDIUM ────────────────────────────────────────────────────────────────
  {
    name: 'Moyen 1', level: 'medium', moves: 3,
    vehicles: [
      { id:1, row:2, col:0, size:2, horizontal:true,  isTarget:true,  color:'#e53935' },
      { id:2, row:0, col:2, size:3, horizontal:false, isTarget:false, color:'#1e88e5' },
      { id:3, row:3, col:2, size:2, horizontal:true,  isTarget:false, color:'#43a047' },
      { id:4, row:0, col:4, size:2, horizontal:false, isTarget:false, color:'#fb8c00' },
      { id:5, row:3, col:5, size:2, horizontal:false, isTarget:false, color:'#6d4c41' },
    ],
  },
  {
    name: 'Moyen 2', level: 'medium', moves: 4,
    vehicles: [
      { id:1, row:2, col:0, size:2, horizontal:true,  isTarget:true,  color:'#e53935' },
      { id:2, row:1, col:2, size:2, horizontal:false, isTarget:false, color:'#1e88e5' },
      { id:3, row:0, col:4, size:3, horizontal:false, isTarget:false, color:'#fb8c00' },
      { id:4, row:0, col:1, size:2, horizontal:true,  isTarget:false, color:'#8e24aa' },
      { id:5, row:4, col:0, size:2, horizontal:true,  isTarget:false, color:'#00acc1' },
    ],
  },
  // ── HARD ──────────────────────────────────────────────────────────────────
  {
    name: 'Difficile 1', level: 'hard', moves: 4,
    vehicles: [
      { id:1, row:2, col:0, size:2, horizontal:true,  isTarget:true,  color:'#e53935' },
      { id:2, row:0, col:2, size:3, horizontal:false, isTarget:false, color:'#1e88e5' },
      { id:3, row:3, col:1, size:2, horizontal:true,  isTarget:false, color:'#43a047' },
      { id:4, row:2, col:4, size:2, horizontal:false, isTarget:false, color:'#fb8c00' },
      { id:5, row:1, col:3, size:3, horizontal:true,  isTarget:false, color:'#8e24aa' },
    ],
  },
  {
    name: 'Difficile 2', level: 'hard', moves: 5,
    vehicles: [
      { id:1, row:2, col:0, size:2, horizontal:true,  isTarget:true,  color:'#e53935' },
      { id:2, row:1, col:2, size:2, horizontal:false, isTarget:false, color:'#1e88e5' },
      { id:3, row:0, col:4, size:3, horizontal:false, isTarget:false, color:'#fb8c00' },
      { id:4, row:0, col:1, size:2, horizontal:true,  isTarget:false, color:'#8e24aa' },
      { id:5, row:3, col:3, size:2, horizontal:true,  isTarget:false, color:'#00acc1' },
      { id:6, row:4, col:0, size:3, horizontal:true,  isTarget:false, color:'#6d4c41' },
    ],
  },
];

export default {
  name: 'RushHourEditor',
  props: { modelValue: { type: Array, default: () => [] } },
  emits: ['update:modelValue'],

  data() {
    return {
      mode: 'preset',
      orientation: 'H',
      size: 2,
      isTarget: false,
      color: PALETTE[0],
      PALETTE,
    };
  },

  computed: {
    localVehicles() {
      const raw = this.modelValue;
      if (!raw) return [];
      return Array.isArray(raw) ? raw : Object.values(raw);
    },
    targetRow() {
      return this.localVehicles.find(v => v.isTarget)?.row ?? 2;
    },
    exitArrowStyle() {
      const U = 100 / 6;
      return { top: `calc(${this.targetRow * U + U / 2}% - 10px)` };
    },
    presetGroups() {
      return [
        { level: 'easy',   label: '🟢 Facile',    presets: PRESETS.filter(p => p.level === 'easy')   },
        { level: 'medium', label: '🟡 Moyen',     presets: PRESETS.filter(p => p.level === 'medium') },
        { level: 'hard',   label: '🔴 Difficile', presets: PRESETS.filter(p => p.level === 'hard')   },
      ];
    },
  },

  methods: {
    applyPreset(preset) {
      // Deep-clone so the preset data is never mutated
      this.$emit('update:modelValue', JSON.parse(JSON.stringify(preset.vehicles)));
    },

    isSelectedPreset(preset) {
      const cur = this.localVehicles;
      if (!cur || cur.length !== preset.vehicles.length) return false;
      return preset.vehicles.every((pv, i) => {
        const cv = cur[i];
        return cv && cv.row === pv.row && cv.col === pv.col && cv.size === pv.size && cv.horizontal === pv.horizontal;
      });
    },

    miniCarStyle(v) {
      const U = 100 / 6;
      const G = 1;
      return {
        position: 'absolute',
        left:       (v.col  * U + G) + '%',
        top:        (v.row  * U + G) + '%',
        width:      (v.horizontal ? v.size * U - G*2 : U - G*2) + '%',
        height:     (v.horizontal ? U - G*2 : v.size * U - G*2) + '%',
        background: v.isTarget ? '#e53935' : v.color,
        borderRadius: '3px',
      };
    },

    toggleTarget() {
      this.isTarget = !this.isTarget;
      if (this.isTarget) {
        this.orientation = 'H';
        this.size = 2;
      }
    },

    buildOccupancy() {
      const g = Array.from({ length: 6 }, () => Array(6).fill(false));
      for (const v of this.localVehicles) {
        for (let i = 0; i < v.size; i++) {
          const r = v.horizontal ? v.row : v.row + i;
          const c = v.horizontal ? v.col + i : v.col;
          if (r >= 0 && r < 6 && c >= 0 && c < 6) g[r][c] = true;
        }
      }
      return g;
    },

    onCellClick(row, col) {
      const horiz = this.isTarget ? true : this.orientation === 'H';
      const sz    = this.isTarget ? 2 : this.size;

      if (horiz && col + sz > 6) return;
      if (!horiz && row + sz > 6) return;

      const occ = this.buildOccupancy();
      for (let i = 0; i < sz; i++) {
        const r = horiz ? row : row + i;
        const c = horiz ? col + i : col;
        if (occ[r][c]) return;
      }

      let updated = [...this.localVehicles];
      if (this.isTarget) updated = updated.filter(v => !v.isTarget);

      updated.push({
        id: Date.now(),
        row,
        col,
        size: sz,
        horizontal: horiz,
        isTarget: this.isTarget,
        color: this.isTarget ? '#e53935' : this.color,
      });

      this.$emit('update:modelValue', updated);
    },

    removeVehicle(id) {
      this.$emit('update:modelValue', this.localVehicles.filter(v => v.id !== id));
    },

    clearAll() {
      this.$emit('update:modelValue', []);
    },

    vehicleOverlayStyle(v) {
      const U = 100 / 6;
      const G = 1.5;
      return {
        left:       (v.col  * U + G) + '%',
        top:        (v.row  * U + G) + '%',
        width:      (v.horizontal ? v.size * U - G*2 : U - G*2) + '%',
        height:     (v.horizontal ? U - G*2 : v.size * U - G*2) + '%',
        background: v.isTarget ? '#e53935' : v.color,
      };
    },
  },
};
</script>

<style scoped>
.rhe-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: rgba(0,0,0,0.15);
  border-radius: 12px;
}

/* ── Mode toggle ────────────────────────────────────────── */
.rhe-mode-row {
  display: flex;
  gap: 8px;
}

.rhe-mode-btn {
  flex: 1;
  padding: 8px 12px;
  border-radius: 9px;
  border: 2px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.7);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}

.rhe-mode-btn.rhe-mode-active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

/* ── Preset section ─────────────────────────────────────── */
.rhe-preset-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.rhe-preset-group-label {
  font-size: 12px;
  font-weight: 800;
  color: rgba(255,255,255,0.6);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.rhe-preset-cards {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.rhe-preset-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 8px;
  border-radius: 10px;
  border: 2px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.06);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, transform 0.1s;
}

.rhe-preset-card:hover {
  border-color: rgba(255,255,255,0.4);
  background: rgba(255,255,255,0.12);
  transform: translateY(-1px);
}

.rhe-preset-card.rhe-preset-selected {
  border-color: #4caf50;
  background: rgba(76,175,80,0.15);
}

/* ── Mini board ─────────────────────────────────────────── */
.rhe-mini-board {
  position: relative;
  width: 84px;
  height: 84px;
  background: #2a3d2a;
  border: 3px solid #4e342e;
  border-radius: 3px;
  flex-shrink: 0;
}

.rhe-preset-name {
  font-size: 12px;
  font-weight: 700;
  color: rgba(255,255,255,0.85);
}

.rhe-preset-moves {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
}

/* ── Toolbar ────────────────────────────────────────────── */
.rhe-toolbar {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rhe-tool-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.rhe-label {
  font-size: 12px;
  font-weight: 700;
  color: rgba(255,255,255,0.7);
  min-width: 70px;
}

.rhe-tb-btn {
  padding: 5px 10px;
  border-radius: 7px;
  border: 1.5px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}

.rhe-tb-btn.rhe-active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.rhe-color-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  transition: transform 0.1s, border-color 0.1s;
}

.rhe-color-dot.rhe-color-sel {
  border-color: white;
  transform: scale(1.25);
}

/* ── Board ──────────────────────────────────────────────── */
.rhe-board-area {
  position: relative;
  display: inline-block;
  align-self: center;
}

.rhe-exit-arrow {
  position: absolute;
  right: -20px;
  color: #ffeb3b;
  font-size: 16px;
  font-weight: 900;
  pointer-events: none;
  filter: drop-shadow(0 0 4px rgba(255,235,59,0.8));
}

.rhe-board {
  position: relative;
  width: 252px;
  height: 252px;
  background: #2a3d2a;
  border: 5px solid #4e342e;
  border-radius: 4px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: repeat(6, 1fr);
}

.rhe-cell {
  border: 0.5px solid rgba(255,255,255,0.1);
  cursor: crosshair;
  transition: background 0.1s;
}

.rhe-cell:hover {
  background: rgba(255,255,255,0.12);
}

/* ── Vehicle overlays ───────────────────────────────────── */
.rhe-v-overlay {
  position: absolute;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.9;
  box-shadow: 0 2px 6px rgba(0,0,0,0.4), inset 0 2px 0 rgba(255,255,255,0.22);
  transition: opacity 0.15s;
  gap: 4px;
}

.rhe-v-overlay:hover {
  opacity: 0.65;
}

.rhe-v-x {
  font-size: 12px;
  color: rgba(255,255,255,0.85);
  font-weight: 700;
}

.rhe-v-flag {
  font-size: 11px;
}

/* ── Footer ─────────────────────────────────────────────── */
.rhe-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.rhe-clear-btn {
  padding: 6px 12px;
  border-radius: 8px;
  border: 1.5px solid #ef5350;
  background: transparent;
  color: #ef5350;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.rhe-clear-btn:hover {
  background: #ef5350;
  color: #fff;
}

.rhe-hint {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  line-height: 1.4;
}
</style>
